import type { ComponentProps } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import JourneyV2Details from 'components/Flights/V2/FareFamily/JourneyV2Details'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import {
  shouldRenderTripDrawerTitleInContent,
  useRightPanelTripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import TextButton from 'LuxKit/Button/TextButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { CurrencyProvider } from 'contexts/currencyContext'
import noop from 'lib/function/noop'
import flightBundleGlossaryMessages from 'i18n/flightBundleGlossaryMessages'

interface Props {
  isOpen: boolean
  journey: App.JourneyV2
  onClose: () => void
  onAfterClose?: () => void
  onSelect?: (data: { journey: App.JourneyV2 }) => void
  selectLabel: string
  showSelectButton: boolean
  isSelectDisabled?: boolean
  isAlreadyAdded?: boolean
}

type FareRulesParams = Parameters<ComponentProps<typeof JourneyV2Details>['setFareRulesParams']>[0]

const DrawerContent = styled(Group)`
  width: 100%;
  max-width: ${rem(700)};
  margin-block: ${rem(20)};
  margin-inline: auto;
  padding-inline: ${rem(16)};
  box-sizing: border-box;
`

const PricingRow = styled(Group)`
  min-height: ${rem(62)};
`

function getFlightRouteTitle(journey: App.JourneyV2): string | undefined {
  const flights = journey.flightGroup.flights
  const firstFlight = flights[0]
  const lastFlight = flights[flights.length - 1]
  if (!firstFlight || !lastFlight) return undefined

  return `${firstFlight.departureAirportName} (${firstFlight.departureAirport}) to ${lastFlight.arrivalAirportName} (${lastFlight.arrivalAirport})`
}

function FlightJourneyDetailsDrawer({
  isOpen,
  journey,
  onClose,
  onAfterClose,
  onSelect = noop,
  selectLabel,
  showSelectButton,
  isSelectDisabled = false,
  isAlreadyAdded = false,
}: Props) {
  const intl = useIntl()
  const drawerPresentation = useRightPanelTripDrawerPresentation()
  const [fareRulesURL, setFareRulesURL] = useState<string | undefined>(undefined)
  const [fareRulesParams, setFareRulesParams] = useState<FareRulesParams | undefined>(undefined)

  const routeTitle = useMemo(() => getFlightRouteTitle(journey), [journey])
  const drawerTitle = intl.formatMessage(flightBundleGlossaryMessages.flightDetails)
  const price = journey.price.adult.totalFare
  const regularPrice = journey.regularPrice?.adult?.totalFare
  const showLuxPlusPrice = journey.bookingInfo.isLuxPlusFare === true && Number.isFinite(regularPrice)
  const showPrice = price != null && !!journey.currencyCode
  const showTitleInContent = shouldRenderTripDrawerTitleInContent(drawerPresentation)
  const selectDisabled = isSelectDisabled || isAlreadyAdded

  const handleHideFareRules = useCallback(() => {
    setFareRulesURL(undefined)
    setFareRulesParams(undefined)
  }, [])

  const handleSelect = useCallback(() => {
    if (selectDisabled) return
    onSelect({ journey })
  }, [journey, onSelect, selectDisabled])

  const addLabel = intl.formatMessage({
    id: 'ktxcA_-NogebFuPXXQlL',
    defaultMessage: 'Add',
    description: 'Short action label to add a flight from the flight details drawer',
  })

  const economyFromLabel = intl.formatMessage({
    id: 'SZllFONGAmlTkxkJaGfP',
    defaultMessage: 'Economy from',
    description: 'Price caption shown above flight price in the flight details drawer',
  })

  const modalHeaderActions = showTitleInContent && showSelectButton ? (
    <AssistChip
      variant="filled"
      size="medium"
      startIcon={<LinePlusIcon />}
      onClick={handleSelect}
      disabled={selectDisabled}
    >
      {addLabel}
    </AssistChip>
  ) : undefined

  const header = showTitleInContent ? undefined : (
    <TripDrawerHeaderTitle title={drawerTitle} subtitle={routeTitle} />
  )

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={onAfterClose}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={header}
      headerActions={modalHeaderActions}
      onBack={fareRulesURL ? handleHideFareRules : undefined}
    >
      <DrawerContent direction="vertical" gap={24}>
        {showTitleInContent && (
          <Group direction="vertical" gap={4}>
            <Heading variant="heading3">{drawerTitle}</Heading>
            {routeTitle && <BodyText variant="large">{routeTitle}</BodyText>}
          </Group>
        )}
        <PricingRow
          direction="horizontal"
          horizontalAlign="space-between"
          verticalAlign="end"
          gap={16}
        >
          {showPrice && (
            <CurrencyProvider currency={journey.currencyCode}>
              <Group direction="vertical" gap={0}>
                <PriceRowPriceCaption>{economyFromLabel}</PriceRowPriceCaption>
                {showLuxPlusPrice ? (
                  <PriceRowLuxPlusPrice size="L" memberPrice={price} nonMemberPrice={regularPrice} saleUnit="adult" />
                ) : (
                  <PriceRowPrice size="L" price={price} saleUnit="adult" />
                )}
              </Group>
            </CurrencyProvider>
          )}
          {showSelectButton && (
            <TextButton kind="primary" size="small" onClick={handleSelect} disabled={selectDisabled}>
              {selectLabel}
            </TextButton>
          )}
        </PricingRow>
        <Divider kind="primary" />
        <JourneyV2Details
          journey={journey}
          fareRulesURL={fareRulesURL}
          fareRulesParams={fareRulesParams}
          setFareRulesURL={setFareRulesURL}
          setFareRulesParams={setFareRulesParams}
        />
      </DrawerContent>
    </TripDrawer>
  )
}

export default FlightJourneyDetailsDrawer
